import React, { createContext, useContext, useEffect, useState } from "react";
import { doc, getDoc, updateDoc, arrayRemove } from "firebase/firestore";
import { useAuth } from "./AuthContext";
import { useAccess } from "./AccessContext";
import { db } from "../Security/firebase-config";
import CustomAlert from "../components/core-components/CustomAlert";
import { hasPersonalFeature } from "../utils/entitlements";

import { useTeam } from "./TeamContext";

const HighlightContext = createContext();

const isHighlightDebugEnabled = () => {
  if (typeof window === "undefined") return false;

  try {
    return window.localStorage.getItem("debugHighlights") === "1";
  } catch {
    return false;
  }
};

const logHighlightDebug = (...args) => {
  if (isHighlightDebugEnabled()) {
    console.log(...args);
  }
};

export const HighlightProvider = ({ children, highlightSource }) => {
  const { userData, loading } = useAuth();
  const { teamId: activeTeamId } = useTeam();
  const { loading: accessLoading, personalEntitlements } = useAccess() || {};
  const [highlights, setHighlights] = useState([]);
  const canUseHighlights = hasPersonalFeature(
    personalEntitlements,
    "personalHighlights"
  );
  
  // ⬅️ NEW: simple alert state + helper
  const [alertData, setAlertData] = useState(null);
  const showAlert = (message, type = "info", position = "top", duration = 4000) => {
    setAlertData({ message, type, position });
    if (duration) {
      window.clearTimeout(showAlert._t);
      showAlert._t = window.setTimeout(() => setAlertData(null), duration);
    }
  };

  useEffect(() => {
    if (loading || accessLoading) return;

    if (!userData?.uid) {
      setHighlights([]);
      return;
    }

    if (!canUseHighlights) {
      setHighlights([]);
      return;
    }

    let isCancelled = false;

    const fetchHighlights = async () => {
      logHighlightDebug(`[DEBUG] fetchHighlights called (highlightSource = ${highlightSource})`);

      try {
        // ---- USER ----
        const userRef = doc(db, "users", userData.uid);
        const userSnapshot = await getDoc(userRef);

        // ✅ extra-safe: never assume data() exists
        const userDataFromDb = userSnapshot.exists() ? (userSnapshot.data() || {}) : {};
        const userHighlights = Array.isArray(userDataFromDb.highlights) ? userDataFromDb.highlights : [];

        // ---- TEAM ----
        let teamHighlights = [];
        if (activeTeamId) {
          const teamRef = doc(db, "teams", activeTeamId);
          const teamSnapshot = await getDoc(teamRef);

          const teamDataFromDb = teamSnapshot.exists() ? (teamSnapshot.data() || {}) : {};
          teamHighlights = Array.isArray(teamDataFromDb.highlights) ? teamDataFromDb.highlights : [];
        }

        // Tag all and merge
        const taggedUserHighlights = userHighlights.map((h) => ({
          ...h,
          savedIn: {
            ...(h?.savedIn || {}),
            user: true,
          },
        }));

        const taggedTeamHighlights = teamHighlights.map((h) => ({
          ...h,
          savedIn: {
            ...(h?.savedIn || {}),
            team: true,
          },
        }));

        const mergedHighlights = [...taggedUserHighlights];

        taggedTeamHighlights.forEach((teamH) => {
          const existing = mergedHighlights.find((h) => h?.id === teamH?.id);
          if (existing) {
            existing.savedIn = existing.savedIn || {};
            existing.savedIn.team = true;
          } else {
            mergedHighlights.push(teamH);
          }
        });

        logHighlightDebug("[DEBUG] Final mergedHighlights:", mergedHighlights);

        // ✅ prevent state update after unmount / rapid changes
        if (!isCancelled) setHighlights(mergedHighlights);
      } catch (error) {
        console.error("[ERROR] Failed to fetch highlights:", error);
        if (!isCancelled) setHighlights([]); // fail-safe
      }
    };

    if (highlightSource === "none") {
      logHighlightDebug("[DEBUG] highlightSource is 'none', clearing highlights");
      setHighlights([]);
    } else {
      fetchHighlights();
    }

    return () => {
      isCancelled = true;
    };
  }, [
    userData?.uid,
    highlightSource,
    loading,
    activeTeamId,
    accessLoading,
    canUseHighlights,
  ]);

  
  

  const addHighlight = async (highlight, target) => {
    if (!userData?.uid) {
      showAlert("Please login to use this function.", "warning", "top");
      return;
    }

    if (!canUseHighlights) {
      showAlert(
        "Join a Basic-or-higher team to save highlights on guide pages.",
        "warning",
        "top"
      );
      return;
    }
  
    // Force `savedIn` before writing
    const fullHighlight = {
      ...highlight,
      savedIn: {
        user: target === "user" || target === "both",
        team: target === "team" || target === "both"
      }
    };
  
    const applyUpdate = async (refPath) => {
      const id = refPath === "users" ? userData.uid : activeTeamId;
      if (!id) return; // no active team selected
      const ref = doc(db, refPath, id);
      const snapshot = await getDoc(ref);
      const highlights = snapshot.exists() ? snapshot.data().highlights || [] : [];
  
      // Remove old version with same ID if it exists
      const withoutOld = highlights.filter(h => h.id !== fullHighlight.id);
      const updated = [...withoutOld, fullHighlight];
  
      await updateDoc(ref, { highlights: updated });
    };
  
    try {
      if (target === "user") {
        await applyUpdate("users");
      } else if (target === "team") {
        await applyUpdate("teams");
      } else if (target === "both") {
        await Promise.all([
          applyUpdate("users"),
          applyUpdate("teams")
        ]);
      }
  
      setHighlights(prev => {
        const withoutOld = prev.filter(h => h.id !== fullHighlight.id);
        return [...withoutOld, fullHighlight];
      });
    } catch (error) {
      console.error("[ERROR] Error adding highlight:", error);
    }
  };
  
  
  
  

  const removeHighlight = async (highlight, target = "user") => {
    if (!userData?.uid) return;
    if (!canUseHighlights) return;
    logHighlightDebug("[DEBUG] removeHighlight called with:", highlight, target);
  
    const matchById = (array) =>
      array.find((h) => h.id === highlight.id);
  
    try {
      if (target === "user" || target === "both") {
        const userRef = doc(db, "users", userData.uid);
        const userSnap = await getDoc(userRef);
        const userHighlights = userSnap.exists() ? userSnap.data().highlights || [] : [];
  
        const exactUserHighlight = matchById(userHighlights);
        if (exactUserHighlight) {
          await updateDoc(userRef, { highlights: arrayRemove(exactUserHighlight) });
          logHighlightDebug("[DEBUG] Removed highlight from user collection:", exactUserHighlight);
        } else {
          console.warn("[WARN] Highlight not found in user collection for removal.");
        }
      }
  
      if ((target === "team" || target === "both") && activeTeamId) {
        const teamRef = doc(db, "teams", activeTeamId);
        const teamSnap = await getDoc(teamRef);
        const teamHighlights = teamSnap.exists() ? teamSnap.data().highlights || [] : [];
  
        const exactTeamHighlight = matchById(teamHighlights);
        if (exactTeamHighlight) {
          await updateDoc(teamRef, { highlights: arrayRemove(exactTeamHighlight) });
          logHighlightDebug("[DEBUG] Removed highlight from team collection:", exactTeamHighlight);
        } else {
          console.warn("[WARN] Highlight not found in team collection for removal.");
        }
      }
  
      // Remove from local state
      setHighlights((prev) => prev.filter((h) => h.id !== highlight.id));
    } catch (error) {
      console.error("[ERROR] Error removing highlight:", error);
    }
  };
  
  
  

  return (
    <HighlightContext.Provider value={{ highlights, addHighlight, removeHighlight, highlightSource }}>
      {alertData && (
        <CustomAlert
          message={alertData.message}
          type={alertData.type}
          position={alertData.position}
          onClose={() => setAlertData(null)}
        />
      )}
      {children}
    </HighlightContext.Provider>
  );  
};

export const useHighlight = () => useContext(HighlightContext);
